<template>
<PageSection>
    <PageHeader id="page-header" title="Financial Aid"></PageHeader>

    <!-- Loading State -->
    <div v-if="isLoadingStore && !storeData && !storeDetailError" class="flex items-center justify-center py-20">
        <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Loading store information...</p>
        </div>
    </div>

    <!-- No Store State -->
    <div v-else-if="!storeData && (!storeDetailError || storeDetailError.message?.includes('not found'))" class="bg-white border rounded-lg shadow-sm p-8">
        <div class="text-center">
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Store Not Configured</h2>
            <p class="text-gray-600 mb-6">Please configure your store first before managing Financial Aid settings.</p>
            <router-link to="/store/configuration" class="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                Go to Store Configuration
            </router-link>
        </div>
    </div>

    <!-- Error State -->
    <div v-else-if="!storeData && storeDetailError && !storeDetailError.message?.includes('not found')" class="flex items-center justify-center py-20">
        <div class="text-center max-w-md">
            <h2 class="text-xl font-semibold text-red-600 mb-4">Error Loading Store</h2>
            <p class="text-gray-600 mb-6">{{ storeDetailError.message || 'Failed to load store details. Please try again.' }}</p>
            <button @click="retryLoadStore" class="btn btn-secondary px-6 py-3">
                Retry
            </button>
        </div>
    </div>

    <!-- Edit Mode -->
    <div v-if="storeData && isEditMode" class="bg-white border rounded-lg shadow-sm">
        <CreateStore
            mode="update"
            :initial-data="initialStoreData"
            @cancel="cancelEditMode"
            @success="handleUpdateSuccess"
        />
    </div>

    <!-- View Mode -->
    <template v-else-if="storeData && !isEditMode">
        <!-- Basic Configuration -->
        <div class="bg-white border rounded-md shadow mb-4">
            <div class="p-4 border-b relative">
                <h3 class="text-lg font-semibold text-gray-900 mb-1">Basic Configuration</h3>
                <p class="text-sm text-gray-600">Core financial aid and SFA file settings</p>
                <button
                    @click="startEditMode"
                    class="btn btn-primary px-3 py-1.5 text-xs font-medium rounded-md absolute top-4 right-4"
                    :disabled="!!storeDetailError"
                >
                    Edit
                </button>
            </div>
            <div class="p-4">
                <Datalist :list="sfaConfiguration" />
            </div>
        </div>

        <!-- Financial Aid Buckets -->
        <div class="bg-white border rounded-md shadow mb-4">
            <div class="p-4 border-b">
                <h3 class="text-lg font-semibold text-gray-900">Financial Aid Buckets</h3>
                <p class="text-sm text-gray-600 mt-1">Configure financial aid allocation buckets</p>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="border-b bg-gray-50">
                            <th class="px-4 py-3 text-left font-medium text-gray-600 w-8"></th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Code</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Default?</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Usage Type</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Expiry Date</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Taxable?</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Bill To</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Priority</th>
                            <th class="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colspan="10" class="px-4 py-5 text-center text-gray-400 text-sm">
                                Coming Soon
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- SFA Files -->
        <div class="bg-white border rounded-md shadow">
            <div class="p-4 border-b">
                <h3 class="text-lg font-semibold text-gray-900">SFA Files</h3>
                <p class="text-sm text-gray-600 mt-1">Financial Aid file uploads</p>
            </div>
            <SFAFiles :store-number="storeData.store_number" />
        </div>
    </template>
</PageSection>
</template>

<script lang="ts" setup>
import { useQuery, useQueryCache } from "@pinia/colada";
import { computed, ref } from "vue";
import { definePage } from "vue-router/auto";
import CreateStore from "../../components/Store/CreateStore.vue";
import Datalist from "../../components/Store/Datalist.vue";
import SFAFiles from "../../components/Store/SFAFiles.vue";
import { useToast } from "../../composables/useToast";
import { trpc } from "../../trpc/client";
import { mapStoreToFormData } from "../../utils/store-model";

const isEditMode = ref(false);

definePage({
	meta: {
		requiresAuth: true,
		featureFlag: "store-management",
	},
});

const {
	error: storeDetailError,
	data: storeData,
	isLoading: isLoadingStore,
	refetch: refetchStoreData,
} = useQuery({
	key: ["store"],
	query: () => trpc.storeManagement.getStoreDetails.query({}),
	gcTime: 1000 * 60 * 10,
	staleTime: 1000 * 60 * 5,
});

const { showErrorToast, showSuccessToast } = useToast();
const queryCache = useQueryCache();

const sfaConfiguration = computed(() => {
	if (!storeData?.value) return [];
	return [
		{
			label: "Enable Balance Snapshots",
			value: storeData.value.enable_sfa_balance_snapshots ? "Yes" : "No",
		},
		{
			label: "SFA In File",
			value: storeData.value.config_sfa?.sfa_in_file_name || "Not configured",
		},
		{
			label: "SFA Out File",
			value: storeData.value.config_sfa?.sfa_out_file_name || "Not configured",
		},
		{
			label: "SFA Rename To",
			value:
				storeData.value.config_sfa?.sfa_in_file_rename_to || "Not configured",
		},
	];
});

const handleUpdateSuccess = (storeNumber: number) => {
	showSuccessToast(`Store ${storeNumber} updated successfully!`);
	isEditMode.value = false;
};

const initialStoreData = computed(() =>
	storeData.value ? mapStoreToFormData(storeData.value) : undefined,
);

const startEditMode = () => {
	isEditMode.value = true;
};

const cancelEditMode = () => {
	isEditMode.value = false;
};

const retryLoadStore = async () => {
	try {
		queryCache.invalidateQueries({
			predicate: (query) =>
				Array.isArray(query.key) && query.key[0] === "store",
		});
		await refetchStoreData();
		showSuccessToast("Store data refreshed successfully");
	} catch (error) {
		showErrorToast("Failed to reload store data. Please try again.");
	}
};
</script>

<style scoped>
/* Override fixed viewport height from StoreFileTable.css so SFAFiles fits in the page card */
:deep(.sfa-files-container) {
    height: auto;
    max-height: none;
}
:deep(.scrollable-content-sfa-files) {
    max-height: 420px;
}
</style>
